import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MapPin, Store } from "lucide-react";
import { api } from "../lib/api";
import { BusinessCard } from "../components/Cards";
import { Pagination } from "../components/Pagination";
import { useTerms } from "../lib/useTerms";
import { useSeo } from "../components/VIP";

const inp = "bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-rose-500 text-sm";
const STATES = ["", "Florida", "Alabama", "Mississippi", "Louisiana", "Texas"];

export default function Businesses() {
  useSeo("Businesses | Gulf Coast VIP", "Discover Gulf Coast businesses — tour operators, hospitality groups, entertainment companies and more.");
  const [params] = useSearchParams();
  const [data, setData] = useState({ items: [], pages: 1, total: 0 });
  const [q, setQ] = useState(params.get("q") || "");
  const [type, setType] = useState(params.get("type") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [state, setState] = useState(params.get("state") || "");
  const [page, setPage] = useState(1);
  const types = useTerms("business", "business_type", ["Hospitality Group", "Tour Operator", "Entertainment Company", "Restaurant Group", "Rental Company", "Attraction Operator", "Retail", "Other"]);

  useEffect(() => {
    const p = new URLSearchParams({ page, limit: 12 });
    if (q) p.set("q", q);
    if (type) p.set("business_type", type);
    if (city) p.set("city", city);
    if (state) p.set("state", state);
    const t = setTimeout(() => api.get(`/businesses?${p}`).then((r) => setData(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, type, city, state, page]);

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12" data-testid="businesses-page">
      <p className="overline mb-2">Local operators & groups</p>
      <h1 className="font-display font-black text-4xl sm:text-5xl mb-8 flex items-center gap-3"><Store className="text-rose-400" size={36} /> Businesses</h1>
      <div className="flex flex-wrap gap-3 mb-8">
        <div className={`${inp} flex items-center gap-2 flex-1 min-w-[200px]`}>
          <Search size={15} className="text-zinc-500" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search businesses..." className="bg-transparent outline-none w-full" data-testid="businesses-search" />
        </div>
        <select className={inp} value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} data-testid="businesses-type-filter">
          <option value="">All types</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className={`${inp} flex items-center gap-2`}>
          <MapPin size={15} className="text-zinc-500" />
          <input value={city} onChange={(e) => { setCity(e.target.value); setPage(1); }} placeholder="City" className="bg-transparent outline-none w-28" data-testid="businesses-city-filter" />
        </div>
        <select className={inp} value={state} onChange={(e) => { setState(e.target.value); setPage(1); }} data-testid="businesses-state-filter">
          {STATES.map((s) => <option key={s} value={s}>{s || "All states"}</option>)}
        </select>
      </div>
      <p className="text-sm text-zinc-500 mb-5">{data.total} business{data.total === 1 ? "" : "es"}</p>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {data.items.map((b) => <BusinessCard key={b.id} b={b} />)}
      </div>
      {data.items.length === 0 && <p className="text-zinc-500 text-center py-16">No businesses found — try different filters.</p>}
      <div className="mt-10"><Pagination page={page} pages={data.pages} onPage={setPage} /></div>
    </div>
  );
}
